// Root-level provider that owns file uploads to a project.
//
// Lifted above the routes so an upload survives navigation: the operator can
// leave the Files tab mid-batch and the floating pill keeps reporting it.
// Multiple concurrent submits are tracked in a Map keyed by submitId, and the
// pill renders one entry per active state. Each uploaded file's extraction runs
// on its own once its upload completes; nothing here starts an agent run.
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "react-query";
import { DEFAULT_UPLOAD_KIND } from "../api";
import { getDriveFileId, getDrivePath, getDriveSource } from "../api/drive";
import { queryKeys } from "../api/queryKeys";
import { useAgentsHttp } from "../api/useAgentsHttp";
import {
  MULTIPART_THRESHOLD_BYTES,
  uploadFileViaMultipart,
} from "../api/uploadMultipart";
import { withTransientRetry } from "../api/transientRetry";
import {
  UploadSubmitContext,
  type UploadSubmitContextValue,
  type UploadSubmitInput,
  type UploadSubmitState,
} from "./useUploadSubmitContext";

interface UploadInitiateResponse {
  upload_id: string;
  // null when ``already_attached=true`` - the BE recognised the file
  // by ``(project_id, original_name)`` and is returning the existing
  // upload_id instead of issuing a fresh PUT URL.
  presigned_url: string | null;
  already_attached?: boolean;
}

const guessContentType = (name: string): string => {
  const lower = name.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".docx"))
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (lower.endsWith(".xlsx"))
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (lower.endsWith(".doc")) return "application/msword";
  if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".eml")) return "message/rfc822";
  return "application/octet-stream";
};

// In-tab uniqueness is enough: the provider lives at AppLayout scope and
// never shares state across tabs.
let _submitIdCounter = 0;
const _newSubmitId = (): string => {
  _submitIdCounter += 1;
  return `submit-${Date.now()}-${_submitIdCounter}`;
};

export const UploadSubmitProvider = ({ children }: { children: ReactNode }) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  // Keyed by submitId. Object key insertion order keeps the pill stack in
  // submit order.
  const [submitsById, setSubmitsById] = useState<Record<string, UploadSubmitState>>(
    {},
  );

  // Warn before closing the tab / hard-reloading while an upload is in flight.
  useEffect(() => {
    const anyInFlight = Object.values(submitsById).some((s) => s.stage === "uploading");
    if (!anyInFlight) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [submitsById]);

  const submit = useCallback(
    async (input: UploadSubmitInput): Promise<void> => {
      const submitId = _newSubmitId();
      const projectLabel = input.project_name || input.project_id;

      // Only touches this submit's slot, and is a no-op once dismissed, so a
      // late update can't resurrect a dismissed pill.
      const updateThis = (updater: (s: UploadSubmitState) => UploadSubmitState) => {
        setSubmitsById((prev) => {
          const current = prev[submitId];
          if (!current) return prev;
          return { ...prev, [submitId]: updater(current) };
        });
      };

      setSubmitsById((prev) => ({
        ...prev,
        [submitId]: {
          submitId,
          stage: "uploading",
          label: `Preparing to upload ${input.files.length} file${
            input.files.length === 1 ? "" : "s"
          } for ${projectLabel}…`,
          projectId: input.project_id,
          projectName: input.project_name || null,
          error: null,
          uploadProgress: { current: 0, total: input.files.length },
          submitStartedAt: Date.now(),
        },
      }));

      try {
        // Upload each FILENAME once. The BE treats (project_id, original_name) as
        // one file - every initiate replaces a prior completed upload of the same
        // name - so a second same-name entry would only delete the first.
        const seenNames = new Set<string>();
        let uploaded = 0;
        for (let i = 0; i < input.files.length; i++) {
          const f = input.files[i];
          if (seenNames.has(f.name)) continue;
          seenNames.add(f.name);
          const kind = DEFAULT_UPLOAD_KIND;
          updateThis((s) => ({
            ...s,
            label: `Uploading file ${i + 1} of ${input.files.length}: ${f.name}`,
            uploadProgress: { current: i + 1, total: input.files.length },
          }));
          const contentType = f.type || guessContentType(f.name);
          const driveFileId = getDriveFileId(f);
          const source = getDriveSource(f) ?? "local";

          if (f.size > MULTIPART_THRESHOLD_BYTES) {
            await uploadFileViaMultipart({
              file: f,
              project_id: input.project_id,
              content_type: contentType,
              source,
              drive_file_id: driveFileId,
              drive_path: getDrivePath(f),
              kind,
              http,
              onProgress: (done, total) => {
                updateThis((s) => ({
                  ...s,
                  label: `Uploading file ${i + 1} of ${input.files.length}: ${f.name} (chunk ${done}/${total})`,
                }));
              },
            });
          } else {
            // Retry transient failures (a throttle-503, a dropped connection) so
            // one blip doesn't abort the batch. initiate mints a fresh upload_id
            // each call, so a retry is safe; complete is idempotent.
            const init = await withTransientRetry(() =>
              http<UploadInitiateResponse>("/uploads/initiate", {
                project_id: input.project_id,
                original_name: f.name,
                content_type: contentType,
                source,
                drive_file_id: driveFileId,
                drive_path: getDrivePath(f),
              }),
            );
            if (!init.already_attached && init.presigned_url !== null) {
              const presignedUrl = init.presigned_url;
              await withTransientRetry(async () => {
                const putResp = await fetch(presignedUrl, {
                  method: "PUT",
                  body: f,
                  headers: { "content-type": contentType },
                });
                if (!putResp.ok) {
                  const detail = await putResp.text();
                  const e = new Error(
                    `S3 PUT failed for ${f.name}: ${putResp.status} ${detail}`,
                  ) as Error & { status: number };
                  e.status = putResp.status;
                  throw e;
                }
              });
              await withTransientRetry(() =>
                http("/uploads/complete", {
                  upload_id: init.upload_id,
                  kind,
                }),
              );
            }
          }
          uploaded += 1;
          // An open Files tab sees each upload land without a manual refresh.
          qc.invalidateQueries({ queryKey: queryKeys.uploadsList(input.project_id) });
        }

        // Re-picked unchanged files are already attached: nothing to upload.
        const reused = (input.reuse_uploads ?? []).filter(
          (r) => !seenNames.has(r.original_name),
        ).length;
        const total = uploaded + reused;
        updateThis((s) => ({
          ...s,
          stage: "done",
          label: `Uploaded ${total} file${total === 1 ? "" : "s"} to ${projectLabel}. Each is read in the background.`,
          error: null,
        }));
        qc.invalidateQueries({ queryKey: queryKeys.uploadsList(input.project_id) });
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        updateThis((s) => ({
          ...s,
          stage: "error",
          label: `Upload failed for ${projectLabel}: ${error.message.slice(0, 200)}`,
          error,
        }));
        throw error;
      }
    },
    [http, qc],
  );

  const dismiss = useCallback((submitId: string) => {
    setSubmitsById((prev) => {
      const current = prev[submitId];
      if (!current || current.stage === "uploading") return prev;
      const next = { ...prev };
      delete next[submitId];
      return next;
    });
  }, []);

  const submits = useMemo(() => Object.values(submitsById), [submitsById]);

  const value = useMemo<UploadSubmitContextValue>(
    () => ({ submits, submit, dismiss }),
    [submits, submit, dismiss],
  );

  return (
    <UploadSubmitContext.Provider value={value}>{children}</UploadSubmitContext.Provider>
  );
};
